/**
 * InvestmentPropertyStrategyAllocator.js – allocate sale proceeds from selling an investment property (CGT, RAD, new home, downsizer, etc.).
 * Used by AgedCareAnalyser. Props: factFind, details, setDetails, radRoomCost (optional room / RAD cap for Fund the RAD slider).
 * CGT uses assessable income + assessable capital gain for marginal rate in year of sale (Section 11 fix).
 */
import React, { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Building, Home, Wallet, Gift, ShieldCheck } from 'lucide-react';
import { CurrencyInput, AllocationInput } from '../../components/ui/index.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import {
    calculateInvestmentPropertyDisposalTax,
    getEffectiveDisposalCgt,
} from '../../../../shared/src/calculation/entityDisposalTax.ts';
import { isEntityOwnership, getEntityIdFromOwnership } from '../../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { ENTITY_STRUCTURE_FUNDING_DISCLAIMER } from '../../utils/entityFundingUtils.ts';
import { routesIpProceedsToEntityPool } from '../../utils/entityInvestmentPropertySale.ts';
import type { InvestmentPropertySaleDetails } from '../../utils/entityInvestmentPropertySale.ts';
import { DOWNSIZER_CONTRIBUTION_CAP, FUNERAL_BOND_LIMIT } from '../../constants/index.ts';
import { DisposalCgtEstimatePanel } from './DisposalCgtEstimatePanel.tsx';
import type { DisposalTaxSummary } from './DisposalCgtEstimatePanel.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind, FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';
import DownsizerRulesAndInsights from '../../components/strategy/DownsizerRulesAndInsights.tsx';
import { strategyExplainers } from '../../strategy/strategyExplainers.ts';

export interface InvestmentPropertyStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: InvestmentPropertySaleDetails;
    setDetails: Dispatch<SetStateAction<InvestmentPropertySaleDetails>>;
    radRoomCost?: number;
}

function InvestmentPropertyStrategyAllocator({ factFind, details, setDetails, radRoomCost = 0 }: InvestmentPropertyStrategyAllocatorProps) {

    const selectedProperty = factFind.assets?.investmentProperty?.find(p => p.id === details.selectedPropertyId) || factFind.assets?.investmentProperty?.[0];

    // DraftFactFind is structurally compatible with FactFind/FactFindAsset (see
    // types/formDraft.ts); the shared calc-engine fn defensively parseFloat/coerces internally
    // regardless (see factFindTypes.ts header), so this cast is safe.
    const rawTaxSummary: DisposalTaxSummary | null = selectedProperty
        ? calculateInvestmentPropertyDisposalTax(factFind as unknown as FactFind, selectedProperty as unknown as FactFindAsset, details)
        : null;
    const taxSummary: DisposalTaxSummary = getEffectiveDisposalCgt(rawTaxSummary, details.cgtOverrideTotal);
    const salePrice = taxSummary?.salePrice ?? (Number(details.salePrice) || parseFloat(String(selectedProperty?.value)) || 0);
    const sellingCosts = taxSummary?.sellingCosts ?? salePrice * 0.025;
    const isPreCGT = taxSummary?.isPreCGT ?? false;
    const netProceeds = taxSummary?.netProceeds ?? salePrice - sellingCosts - (taxSummary?.estimatedCGT ?? 0);
    const radCap = Math.max(0, Number(radRoomCost) || 0);
    const radSliderMax = radCap > 0 ? Math.min(netProceeds, radCap) : netProceeds;

    const handleDetailChange = (field: string, value: number | undefined) => {
        if (field === 'radAllocation') {
            const num = value ?? 0;
            const capped = Number.isFinite(num) ? Math.min(Math.max(0, num), radSliderMax) : 0;
            setDetails(prev => ({ ...prev, radAllocation: capped }));
            return;
        }
        if (field === 'costBase') {
            setDetails(prev => ({ ...prev, costBase: value }));
            return;
        }
        setDetails(prev => ({ ...prev, [field]: value || 0 }));
    };

    const handleCgtOverrideChange = (value: number | null) => {
        setDetails(prev => ({
            ...prev,
            cgtOverrideTotal: value == null ? undefined : value,
        }));
    };

    useEffect(() => {
        setDetails(prev => {
            const r = Number(prev.radAllocation) || 0;
            if (r <= radSliderMax) return prev;
            return { ...prev, radAllocation: radSliderMax };
        });
    }, [radSliderMax, setDetails]);

    const totalAllocated = (Number(details.radAllocation) || 0) +
        (Number(details.newHomePurchasePrice) || 0) +
        (Number(details.downsizerContribution) || 0) +
        (Number(details.giftingAmount) || 0) +
        (Number(details.funeralBondPurchase) || 0);
    const unallocatedProceeds = netProceeds - totalAllocated;

    const clientAge = calculateAge(factFind.client?.dob);
    const isDownsizerEligible = (clientAge ?? 0) >= 55;
    const entityId =
        selectedProperty && isEntityOwnership(selectedProperty.ownership)
            ? getEntityIdFromOwnership(selectedProperty.ownership)
            : null;
    const entityStructure = entityId
        ? (factFind.structures || []).find((s) => s.id === entityId)
        : null;
    const proceedsToEntityPool =
        selectedProperty && routesIpProceedsToEntityPool(factFind, selectedProperty);

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-6">
            <div>
                <h4 className="font-semibold text-lg">1. Select Investment Property</h4>
                {factFind.assets?.investmentProperty && factFind.assets.investmentProperty.length > 1 ? (
                    <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Property to Sell</label>
                        <select
                            value={details.selectedPropertyId || factFind.assets?.investmentProperty?.[0]?.id}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                const selectedId = e.target.value;
                                const selectedProp = factFind.assets?.investmentProperty?.find(p => p.id === selectedId);
                                const costBaseValue = selectedProp?.costBase;
                                const salePriceValue = Number(selectedProp?.value) || 0;
                                const sellingCostsValue = salePriceValue * 0.025;
                                setDetails(prev => ({
                                    ...prev,
                                    selectedPropertyId: selectedId,
                                    salePrice: salePriceValue,
                                    costBase: costBaseValue,
                                    sellingCosts: sellingCostsValue
                                }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {factFind.assets?.investmentProperty?.map(property => (
                                <option key={property.id} value={property.id}>
                                    {property.description || `Investment Property ${property.id}`} - {formatCurrency(parseFloat(String(property.value)) || 0)}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className="mt-2 p-3 bg-white border-l-4 border-blue-400 rounded-r-lg text-sm">
                        <p><strong className="text-blue-700">Selected Property:</strong> {selectedProperty?.description || 'Investment Property'} - {formatCurrency(parseFloat(String(selectedProperty?.value)) || 0)}</p>
                    </div>
                )}
            </div>
            <div>
                <h4 className="font-semibold text-lg">2. Calculate Net Proceeds</h4>
                {/* Four currency fields never fit this column: `md:` fired at a 768px window while
                    the allocator sat in a 619px working column (~145px each), and even at the widest
                    two-pane layout four columns is ~177px. Two columns, two rows — every field stays
                    above 330px at every size. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                    <CurrencyInput label="Sale Price" name="salePrice" value={details.salePrice || ''} onChange={(v) => handleDetailChange('salePrice', v)} />
                    <CurrencyInput label="Cost Base" name="costBase" value={details.costBase || ''} onChange={(v) => handleDetailChange('costBase', v)} placeholder={isPreCGT ? 'N/A Pre CGT' : ''} />
                    <CurrencyInput label="Selling Costs" name="sellingCosts" value={details.sellingCosts || ''} onChange={(v) => handleDetailChange('sellingCosts', v)} />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Net Proceeds</label>
                        <p className="w-full px-4 py-2 bg-gray-200 border border-gray-300 rounded-lg font-bold">{formatCurrency(netProceeds)}</p>
                    </div>
                </div>
                <div className="mt-2">
                    <DisposalCgtEstimatePanel
                        factFind={factFind}
                        taxSummary={rawTaxSummary}
                        overrideValue={details.cgtOverrideTotal}
                        onOverrideChange={handleCgtOverrideChange}
                        entityId={entityId}
                        asset={selectedProperty}
                    />
                </div>
            </div>
            <div>
                <h4 className="font-semibold text-lg">3. Allocate the Proceeds</h4>
                <div className="mt-2 p-3 bg-white border-l-4 border-blue-400 rounded-r-lg text-sm flex justify-between items-center">
                    <p>
                        <strong className="text-blue-700">Unallocated Proceeds:</strong>
                        {proceedsToEntityPool && (
                            <span className="block text-xs font-normal text-blue-800 mt-1">
                                Credited to {entityStructure?.name?.trim() || 'structure'} cash (entity pool), not personal bank
                            </span>
                        )}
                    </p>
                    <p className={`font-bold text-lg ${unallocatedProceeds < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(unallocatedProceeds)}</p>
                </div>
                {proceedsToEntityPool && (
                    <p className="mt-2 text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-lg p-3">
                        {ENTITY_STRUCTURE_FUNDING_DISCLAIMER}
                    </p>
                )}
                <div className="mt-4 space-y-4">
                    <AllocationInput icon={Building} label="Fund the RAD" name="radAllocation" value={details.radAllocation} onChange={handleDetailChange} max={radSliderMax} />
                    <AllocationInput icon={Home} label="Purchase New Home" name="newHomePurchasePrice" value={details.newHomePurchasePrice} onChange={handleDetailChange} max={netProceeds} />
                    <AllocationInput
                        icon={Wallet}
                        label="Downsizer Contribution"
                        name="downsizerContribution"
                        value={details.downsizerContribution}
                        onChange={handleDetailChange}
                        max={DOWNSIZER_CONTRIBUTION_CAP}
                        disabled={!isDownsizerEligible}
                        tooltip={strategyExplainers.downsizerToSuper(isDownsizerEligible)}
                    />
                    <DownsizerRulesAndInsights factFind={factFind} />
                    {factFind.adviceFor === 'couple' && (
                        <div className="mt-2">
                            <AllocationInput
                                icon={Wallet}
                                label="Partner's Downsizer Contribution"
                                name="partnerDownsizerContribution"
                                value={details.partnerDownsizerContribution}
                                onChange={handleDetailChange}
                                max={DOWNSIZER_CONTRIBUTION_CAP}
                                disabled={!isDownsizerEligible}
                                tooltip={strategyExplainers.partnerDownsizerToSuper(isDownsizerEligible)}
                            />
                        </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                        <input type="checkbox" id="commenceIncomeStreamInv" checked={!!details.commenceIncomeStream} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDetails(prev => ({ ...prev, commenceIncomeStream: e.target.checked }))} />
                        <label htmlFor="commenceIncomeStreamInv" className="text-sm">Commence an income stream from downsizer contribution</label>
                    </div>
                    <AllocationInput icon={Gift} label="Gift to Loved Ones" name="giftingAmount" value={details.giftingAmount} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.giftToLovedOnes()} />
                    <AllocationInput icon={ShieldCheck} label="Purchase Funeral Bond" name="funeralBondPurchase" value={details.funeralBondPurchase} onChange={handleDetailChange} max={FUNERAL_BOND_LIMIT} tooltip={strategyExplainers.funeralBond()} />
                </div>
            </div>
        </div>
    );
}

export default InvestmentPropertyStrategyAllocator;
